/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { Stack, Divider, Body, TextLink, H2, H2Library, FlexContainer } from "@carrotfertility/carotene"
import useInsuranceDetails from "./context/useInsuranceDetails"
import InsurancePlanInformationTable from "./plan-information-table"
import { CMDTableContainer } from "components/views/reimbursements/educationmodal/CMDTable"
import { Spacer } from "components/views/atoms/Atoms"
import { useCurrentUser } from "components/context/user/UserContext"
import { useHistory } from "react-router-dom"
import InsuranceExpired from "./InsuranceExpired"
import Locations from "components/views/home/navigation"
import styled from "styled-components"
import { MultiPathSVG } from "components/views/SVG"
import Tooltip from "components/Tooltip"
import NoInsurance from "./NoInsurance"
import { COMPANY_DEDUCTIBLE_STATUS } from "components/context/user/DeductibleContext"
import { useToggle } from "utils/Hooks"
import { EraseYourAnswersModal } from "components/cmd-enrollment/EraseYourAnswersModal"
import { FormattedMessage, defineMessage, useIntl } from "react-intl"

const StyledDivider = styled(Divider)`
  width: 100%;
`
const HelpIconContainer = styled.span`
  display: inline-block;
  vertical-align: text-top;
  color="#e48400" 
`
const StyledList = styled.ul`
  margin-top: 10px;
  margin-left: 25px;
  list-style-position: inside;
`
const StyledListItem = styled.li`
  height: 25px;
  font-size: 16px;
  &:before {
    color: black;
  }
`

const SpacedDivider = (): JSX.Element => (
  <>
    <Spacer height={2} />
    <StyledDivider />
    <Spacer height={2} />
  </>
)

const QleExplanationMessage = (): JSX.Element => {
  const listOptions = [
    defineMessage({ defaultMessage: "loss of coverage" }),
    defineMessage({ defaultMessage: "household changes" }),
    defineMessage({ defaultMessage: "changes in residence" }),
    defineMessage({ defaultMessage: "and more", description: "final list option" })
  ]
  const intl = useIntl()

  return (
    <FlexContainer width="300px" padding="small" direction="column">
      <Body>
        <FormattedMessage defaultMessage="A qualifying life event (QLE) allows you to enroll in health insurance outside the open enrollment period. QLEs include" />
      </Body>
      <StyledList>
        {listOptions.map((textItem, index) => (
          <StyledListItem key={index}>{intl.formatMessage(textItem)}</StyledListItem>
        ))}
      </StyledList>
    </FlexContainer>
  )
}

const InsuranceExpiredMessage = () => (
  <>
    <Spacer height={1.5} />
    <InsuranceExpired />
  </>
)

const DeductibleInfoMessage = () => (
  <>
    <Spacer height={1.5} />
    <Body>
      <FormattedMessage defaultMessage="You and your partner will need to meet annual deductibles before you can be reimbursed for qualified medical expenses." />
    </Body>
  </>
)

const QleResetMessage = (): JSX.Element => {
  const [showEraseAnswersModal, toggleEraseAnswersModal] = useToggle(false)

  return (
    <>
      <Spacer height={1.5} />
      <Body>
        <FormattedMessage
          defaultMessage="Has your plan changed due to a qualifying life event <tooltip></tooltip>, such as getting married or gaining a dependent? <link>Update plan info.</link>"
          values={{
            tooltip: () => (
              <Tooltip content={(): JSX.Element => <QleExplanationMessage />}>
                <HelpIconContainer>
                  <MultiPathSVG iconName="icn-help" />
                </HelpIconContainer>
              </Tooltip>
            ),
            link: (linkContent) => <TextLink onClick={toggleEraseAnswersModal}>{linkContent}</TextLink>
          }}
        />

        {showEraseAnswersModal && <EraseYourAnswersModal onExit={toggleEraseAnswersModal} />}
      </Body>
    </>
  )
}

const NoInsuranceMessage = (): JSX.Element => (
  <>
    <Spacer height={2} />
    <NoInsurance />
  </>
)

const DeductibleTables = (): JSX.Element => {
  return (
    <>
      <Spacer height={2} />
      <CMDTableContainer />
    </>
  )
}

const HealthPlanTable = (): JSX.Element => {
  const userInfo = useCurrentUser()
  const { insurancePlanDetailsData, insuranceExpired, memberHistory } = useInsuranceDetails()
  const userHasPartner =
    insuranceExpired && memberHistory ? memberHistory.memberHasLegalPartner : userInfo.memberHasLegalPartner
  const [showEraseAnswersModal, toggleEraseAnswersModal] = useToggle(false)

  return (
    <>
      <SpacedDivider />
      <Stack sx={{ paddingBottom: "24px" }} spacing="small">
        <H2>
          <FormattedMessage defaultMessage="About your health plan" />
        </H2>
        <Body>
          <FormattedMessage
            defaultMessage="Need to make a correction? <link>Update your answers.</link>"
            values={{ link: (linkContent) => <TextLink onClick={toggleEraseAnswersModal}>{linkContent}</TextLink> }}
          />

          {showEraseAnswersModal && <EraseYourAnswersModal onExit={toggleEraseAnswersModal} />}
        </Body>
      </Stack>
      <InsurancePlanInformationTable
        hasPartner={userHasPartner}
        planData={insurancePlanDetailsData}
        insuranceExpired={insuranceExpired}
      />
    </>
  )
}

const InsuranceDetailsTable = (): JSX.Element => {
  const userInfo = useCurrentUser()
  const { insuranceProviderDetailsData, insuranceExpired, memberHistory } = useInsuranceDetails()
  const userHasPartner =
    insuranceExpired && memberHistory ? memberHistory.memberHasLegalPartner : userInfo.memberHasLegalPartner
  const history = useHistory()
  const handleEditInsuranceAnswers = (): void => history.push(Locations.Home.InsuranceEditEnrollmentActive)
  return (
    <>
      <SpacedDivider />
      <Stack sx={{ paddingBottom: "24px" }} spacing="small">
        <H2>
          <FormattedMessage defaultMessage="Connecting to your insurance provider" />
        </H2>
        <Body>
          <FormattedMessage
            defaultMessage="Need to make a correction? <link>Update your answers.</link>"
            values={{
              link: (linkContent) => <TextLink onClick={handleEditInsuranceAnswers}>{linkContent}</TextLink>
            }}
          />
        </Body>
      </Stack>
      <InsurancePlanInformationTable
        hasPartner={userHasPartner}
        planData={insuranceProviderDetailsData}
        insuranceExpired={insuranceExpired}
      />
    </>
  )
}

const InsuranceDetails = (): JSX.Element => {
  const {
    needsBenefitEnrollment,
    insuranceExpired,
    hasDeductible,
    hasDeductibleRemaining,
    partnerPlanRequiresInsuranceProviderInfo,
    memberPlanRequiresInsuranceProviderInfo,
    hasInsurance,
    companyDeductibleStatus,
    memberHistory
  } = useInsuranceDetails()

  const isEnrolled = !needsBenefitEnrollment

  return (
    <FlexContainer direction="column" sx={{ marginTop: "40px", maxWidth: "685px" }}>
      <H2Library>
        <FormattedMessage defaultMessage="About your insurance" />
      </H2Library>
      {insuranceExpired && <InsuranceExpiredMessage />}
      {hasDeductibleRemaining && isEnrolled && <DeductibleInfoMessage />}
      {isEnrolled && <QleResetMessage />}
      {!hasInsurance && <NoInsuranceMessage />}
      {!insuranceExpired && hasDeductible && <DeductibleTables />}
      {(insuranceExpired || (isEnrolled && hasInsurance)) && (
        <>
          <HealthPlanTable />
          {(partnerPlanRequiresInsuranceProviderInfo ||
            memberPlanRequiresInsuranceProviderInfo ||
            (insuranceExpired && memberHistory && (memberHistory.memberHasHDHP || memberHistory.partnerHasHDHP)) ||
            companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL) && <InsuranceDetailsTable />}
        </>
      )}
    </FlexContainer>
  )
}

export default InsuranceDetails
